import WebUploader from "webuploader";
import "webuploader/dist/webuploader.css";

import Evented from '@/utils/Evented.js';
import { Store } from "@/utils/store";

import Message from '@/utils/resetMessage';
import { getToken } from "@/utils/generalMethods";
import logServeCof from '@/plugins/logServeCof.js';

import { createAnnotationAttachmentUploadService, createAnchorAttachmentUploadService } from "@/service/interface";

const BLOB_SIZE = 5 * 1024 * 1024;

const MAX_FILE_SIZE = 1 * 1024 * 1024 * 1024;

const AppendInterfaceMap = {
    'Annotation': 'annotation/attachment/append',
    'Anchor': 'anchorPoint/attachment/append',
}

class AttachmentUploader extends Evented {
    constructor(schemeType, checkFilesCount, isSameFileCheck) {
        super();
        this.schemeType = schemeType;
        this.checkFilesCount = checkFilesCount
        this.isSameFileCheck = isSameFileCheck;

        // status为前端自定义状态，状态说明如下：
        // waiting：某个文件等待上传
        // created: 已创建上传索引
        // paused: 已暂停
        // continue：某个文件断点续传
        // update：某个文件重新上传
        this.status = "waiting";

        this.uploadFiles = [];          // 等待上传的文件
        this.uploadingFiles = [];       // 上传中的文件 

        this.currFileInfo = null;

        this.onFilesQueued = this.onFilesQueued.bind(this);
        this.onUploadBeforeSend = this.onUploadBeforeSend.bind(this);
        this.onUploadSuccess = this.onUploadSuccess.bind(this);
        this.onUploadError = this.onUploadError.bind(this);

        this.initUploader();
        this.addUploaderEventsListener();
    }

    initUploader() {
        const { uploaders } = Store;
        const find = uploaders.find(el => el.schemeType === this.schemeType);
        if (!find) {
            this.uploader = this.createUploader();
            uploaders.push({ schemeType: this.schemeType, uploader: this });
        }
    }

    createUploader() {
        let webUploader = WebUploader.create({
            // swf文件路径
            swf: "../../node_modules/webuploader/dist/Uploader.swf",

            // 文件接收服务端。
            server: `${window.serverUrl}/${logServeCof.logServeCof()}/project/scene/v3/${AppendInterfaceMap[this.schemeType]}`,
            timeout: window.uploadTimeout,

            accept: {
                title: "文件上传",
                extensions: "*",
                mimeTypes: ".docx,.xlsx,.pdf,.txt,.zip",
            },
            compress: false,
            chunked: true, // 分片处理大文件
            chunkSize: BLOB_SIZE,
            fileVal: "FileInfo",
            threads: 1,
            chunkRetry: 0,
            duplicate: true,

            auto: false,
        });
        return webUploader;
    }

    addUploaderEventsListener() {
        this.uploader.on("filesQueued", this.onFilesQueued);
        this.uploader.on("uploadBeforeSend", this.onUploadBeforeSend);
        this.uploader.on("uploadSuccess", this.onUploadSuccess);
        this.uploader.on("uploadError", this.onUploadError);
    }

    onFilesQueued(files) {
        let invalidFiles = [];
        let sameFiles = [];
        files.forEach(file => {
            file.fileName = file.name;
            file.length = file.size;
            file.fileSize = file.size;
            file.schemeType = this.schemeType;

            let fileInfo = {
                id: WebUploader.Base.guid(),
                schemeType: this.schemeType,
                // 文件状态，前端自定义(waiting: 等待上传, created: 已创建上传索引, paused: 已暂停, continue: 断点续传, update: 文件更新)
                customStatus: this.status,

                fileName: file.name,
                fileSize: file.size,
                length: file.size,
                rate: 0,

                isInvalid: false,

                // 文件对象
                file,
            }

            if (!this.isSameFileCheck(fileInfo)) {
                fileInfo.isInvalid = true
                sameFiles.push(fileInfo);
            }

            if (file.size > MAX_FILE_SIZE) {
                fileInfo.isInvalid = true;
                fileInfo.invalidType = 'largeFile';
                invalidFiles.push(fileInfo);
            }


            if (!fileInfo.isInvalid) {
                this.uploadFiles.push(fileInfo);
            }
        })

        if (sameFiles.length && invalidFiles.length) {
            Message.warning('存在同名和超出1GB的文件, 将不会上传');
        } else if (sameFiles.length && !invalidFiles.length) {
            Message.warning('存在同名文件，同名文件将不会上传');
        } else if (!sameFiles.length && invalidFiles.length) {
            Message.warning('文件大小不能超过1GB，超出1GB的文件将不会上传');
        }

        this.checkFilesCount(this.uploadFiles).then(isCheckPass => {
            if (isCheckPass) {
                if (this.uploadFiles.length) {
                    this.startUploadFiles();
                }
            } else {
                this.uploadFiles = []
            }
        })
    }

    onUploadBeforeSend(object, data, headers) {
        headers["Authorization"] = getToken();

        data["Index"] = data.chunk ? data.chunk : 0;

        let fileInfo = {
            fileName: data.name,
            fileSize: data.size
        }
        let find = this.uploadingFiles.find((el) => this.isSameFile(el, fileInfo));
        if (find) {
            let rate = ((object.chunk + 1) / object.chunks) * 10000;
            rate = Math.floor(rate) / 100;
            find.rate = rate;
        }
        this.fire('updateAttachmentList', {});
    }

    onUploadSuccess(file) {
        let findIndex = this.uploadingFiles.findIndex((el) => this.isSameFile(el, file));
        if (findIndex !== -1) {
            let fileInfo = this.uploadingFiles[findIndex];
            this.fire('attachmentUploadSuccess', { fileInfo });

            this.uploadingFiles.splice(findIndex, 1);
            this.uploader.removeFile(file, true);
            this.uploader.stop();
            this.status === "waiting";

            if (this.uploadFiles.length) {
                let uploadingFile = this.uploadFiles.shift();
                this.uploadingFiles.push(uploadingFile);
                this.handleUploadSuccess(uploadingFile);
            }
        }
    }

    onUploadError(file) {
        let findIndex = this.uploadingFiles.findIndex((el) => this.isSameFile(el, file));
        if (findIndex !== -1) {
            this.uploadingFiles.splice(findIndex, 1);
            this.uploader.removeFile(file, true);
            this.uploader.stop();
            this.status === "waiting";

            if (this.uploadFiles.length) {
                let uploadingFile = this.uploadFiles.shift();
                this.uploadingFiles.push(uploadingFile);
                this.handleUploadSuccess(uploadingFile);
            }
        }
    }

    startUploadFiles() {
        if (this.uploadingFiles.length) {
            Message.warning("当前有文件正在上传，请耐心等待");
            return;
        }

        let uploadingFile = this.uploadFiles.shift();

        if (uploadingFile.customStatus === 'waiting') {
            this.uploadingFiles.push(uploadingFile);

            this.createFileUpLoad(uploadingFile).then(() => {
                this.uploader.upload();
            });
        }
    }

    createFileUpLoad(fileInfo) {
        return new Promise(resolve => {
            let count = fileInfo.fileSize / (BLOB_SIZE);
            let params = {
                fileName: fileInfo.fileName,
                fileSize: fileInfo.fileSize,
                blobCount: Math.ceil(count),
                blobSize: BLOB_SIZE,
            };

            let headers = {
                Authorization: getToken()
            }
            switch (this.schemeType) {
                case "Annotation": {
                    createAnnotationAttachmentUploadService(params, { headers }).then(response => {
                        if (response.isSuccess) {
                            let result = response.data;

                            this.uploader.options.formData["FileDataId"] = result.fileDataId;
                            this.uploader.options.formData["Key"] = result.fileKey;

                            fileInfo["fileDataId"] = result.fileDataId;
                            fileInfo["Key"] = result.fileKey;

                            this.fire('attachmentStartUploadFile', { fileInfo });

                            resolve(result);
                        } else {
                            Message.error(response.errMsg);
                            this.handleFileCreateFail(fileInfo);
                        }
                    })
                    break;
                }
                case 'Anchor': {
                    createAnchorAttachmentUploadService(params, { headers }).then(response => {
                        if (response.isSuccess) {
                            let result = response.data;

                            this.uploader.options.formData["FileDataId"] = result.fileDataId;
                            this.uploader.options.formData["Key"] = result.fileKey;

                            fileInfo["fileDataId"] = result.fileDataId;
                            fileInfo["Key"] = result.fileKey;

                            this.fire('attachmentStartUploadFile', { fileInfo });

                            resolve(result);
                        } else {
                            Message.error(response.errMsg);
                            this.handleFileCreateFail(fileInfo);
                        }
                    })
                    break;
                }
            }
        })
    }

    handleUploadSuccess(uploadingFile) {
        if (uploadingFile.customStatus === 'waiting') {
            this.createFileUpLoad(uploadingFile).then(() => {
                // 延时执行以触发文件上传事件
                setTimeout(() => {
                    this.uploader.upload();
                }, 500);
            });
        }
    }

    handleFileCreateFail(fileInfo) {
        let findIndex = this.uploadingFiles.findIndex(el => this.isSameFile(el, fileInfo))
        if (findIndex !== -1) {
            this.uploadingFiles.splice(findIndex, 1)
            this.uploader.removeFile(fileInfo.file, true);
        }

        if (this.uploadFiles.length) {
            let uploadingFile = this.uploadFiles.shift();
            this.uploadingFiles.push(uploadingFile);
            this.handleUploadSuccess(uploadingFile);
        }
    }

    setStatus(status) {
        this.status = status;
    }

    isSameFile(fileInfo1, fileInfo2) {
        return fileInfo1.fileName === fileInfo2.fileName && fileInfo1.fileSize === fileInfo2.fileSize
    }
}

export default AttachmentUploader;